import { isMultiSelectDef, isStringDef } from './narrow.js';
/**
 * Builds a frozen registry from category arrays. Returns the input array
 * unchanged at runtime (just frozen) so callers retain the literal-type
 * inference produced by `defineFeature`.
 */
export function buildRegistry(defs) {
    Object.freeze(defs);
    return defs;
}
/**
 * Runtime consistency check for the registry. Call once at boot.
 *
 * Severity is caller-controlled - intended pattern is fail-fast in dev/CI
 * (throw) and log-and-continue in prod (catch + logger.error). We don't
 * import a logger here because this package must remain free of app-level
 * dependencies.
 */
function validateUniqueNames(defs, errors) {
    const propNames = new Set();
    const uiNames = new Set();
    for (const def of defs) {
        if (propNames.has(def.propName)) {
            errors.push(`Duplicate propName: ${def.propName}`);
        }
        propNames.add(def.propName);
        if (uiNames.has(def.uiName)) {
            errors.push(`Duplicate uiName: ${def.uiName}`);
        }
        uiNames.add(def.uiName);
    }
}
function validateMultiSelectOptions(defs, errors) {
    for (const def of defs) {
        if (!isMultiSelectDef(def))
            continue;
        const validValues = new Set(def.validValues);
        const optionKeys = Object.keys(def.options);
        for (const key of optionKeys) {
            if (!validValues.has(key)) {
                errors.push(`Feature ${def.propName}: option '${key}' is not in validValues`);
            }
        }
        for (const v of def.validValues) {
            if (!(v in def.options)) {
                errors.push(`Feature ${def.propName}: validValue '${v}' has no matching option entry`);
            }
        }
    }
}
function validateRequiredFeatures(defs, propNames, errors) {
    for (const def of defs) {
        if (!def.requiresFeature)
            continue;
        for (const required of def.requiresFeature) {
            if (!propNames.has(required)) {
                errors.push(`Feature ${def.propName} requires unknown feature '${required}'`);
            }
        }
    }
}
function validatePrerequisites(defs, propNames, errors) {
    for (const def of defs) {
        if (!isMultiSelectDef(def))
            continue;
        const options = def.options;
        for (const [optionKey, cfg] of Object.entries(options)) {
            if (cfg.prerequisite && !propNames.has(cfg.prerequisite)) {
                errors.push(`Feature ${def.propName} option '${optionKey}' has unknown prerequisite '${cfg.prerequisite}'`);
            }
        }
    }
}
function validateValidValuesFrom(defs, errors) {
    for (const def of defs) {
        if (!isStringDef(def) || !def.validValuesFrom)
            continue;
        const source = defs.find((d) => d.propName === def.validValuesFrom);
        if (!source) {
            errors.push(`Feature ${def.propName} sources validValues from unknown feature '${def.validValuesFrom}'`);
        }
        else if (!isMultiSelectDef(source)) {
            errors.push(`Feature ${def.propName} sources validValues from '${def.validValuesFrom}' which is not a MultiSelect`);
        }
    }
}
export function assertRegistryConsistent(defs) {
    const errors = [];
    const propNames = new Set(defs.map((d) => d.propName));
    validateUniqueNames(defs, errors);
    validateMultiSelectOptions(defs, errors);
    validateRequiredFeatures(defs, propNames, errors);
    validatePrerequisites(defs, propNames, errors);
    validateValidValuesFrom(defs, errors);
    if (errors.length > 0) {
        throw new Error(`Feature registry inconsistencies:\n  - ${errors.join('\n  - ')}`);
    }
}
